import type { Vec2 } from './types';

/**
 * Closed centripetal Catmull-Rom spline (alpha = 0.5, which never forms cusps
 * or self-intersections inside a segment). Knot spacing uses sqrt(sqrt(d^2)),
 * which is exact in every JS engine.
 */
function knot(a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(Math.sqrt(dx * dx + dy * dy));
}

function evalSegment(p0: Vec2, p1: Vec2, p2: Vec2, p3: Vec2, t0: number, t1: number, t2: number, t3: number, t: number): Vec2 {
  const a1x = ((t1 - t) / (t1 - t0)) * p0.x + ((t - t0) / (t1 - t0)) * p1.x;
  const a1y = ((t1 - t) / (t1 - t0)) * p0.y + ((t - t0) / (t1 - t0)) * p1.y;
  const a2x = ((t2 - t) / (t2 - t1)) * p1.x + ((t - t1) / (t2 - t1)) * p2.x;
  const a2y = ((t2 - t) / (t2 - t1)) * p1.y + ((t - t1) / (t2 - t1)) * p2.y;
  const a3x = ((t3 - t) / (t3 - t2)) * p2.x + ((t - t2) / (t3 - t2)) * p3.x;
  const a3y = ((t3 - t) / (t3 - t2)) * p2.y + ((t - t2) / (t3 - t2)) * p3.y;
  const b1x = ((t2 - t) / (t2 - t0)) * a1x + ((t - t0) / (t2 - t0)) * a2x;
  const b1y = ((t2 - t) / (t2 - t0)) * a1y + ((t - t0) / (t2 - t0)) * a2y;
  const b2x = ((t3 - t) / (t3 - t1)) * a2x + ((t - t1) / (t3 - t1)) * a3x;
  const b2y = ((t3 - t) / (t3 - t1)) * a2y + ((t - t1) / (t3 - t1)) * a3y;
  return {
    x: ((t2 - t) / (t2 - t1)) * b1x + ((t - t1) / (t2 - t1)) * b2x,
    y: ((t2 - t) / (t2 - t1)) * b1y + ((t - t1) / (t2 - t1)) * b2y,
  };
}

/** Dense samples along the closed spline through `pts` (in order). */
export function sampleClosedCatmullRom(pts: Vec2[], perSegment: number): Vec2[] {
  const n = pts.length;
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const t0 = 0;
    const t1 = t0 + knot(p0, p1);
    const t2 = t1 + knot(p1, p2);
    const t3 = t2 + knot(p2, p3);
    for (let k = 0; k < perSegment; k++) {
      out.push(evalSegment(p0, p1, p2, p3, t0, t1, t2, t3, t1 + ((t2 - t1) * k) / perSegment));
    }
  }
  return out;
}

/** Resample a closed polyline at (nearly) uniform arc-length spacing. */
export function resampleClosed(dense: Vec2[], spacing: number): Vec2[] {
  const m = dense.length;
  const cum = new Array<number>(m + 1);
  cum[0] = 0;
  for (let i = 0; i < m; i++) {
    const a = dense[i];
    const b = dense[(i + 1) % m];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    cum[i + 1] = cum[i] + Math.sqrt(dx * dx + dy * dy);
  }
  const total = cum[m];
  const n = Math.max(16, Math.round(total / spacing));
  const ds = total / n;
  const out: Vec2[] = [];
  let j = 0;
  for (let k = 0; k < n; k++) {
    const target = k * ds;
    while (j < m - 1 && cum[j + 1] < target) j++;
    const seg = cum[j + 1] - cum[j];
    const t = seg > 0 ? (target - cum[j]) / seg : 0;
    const a = dense[j];
    const b = dense[(j + 1) % m];
    out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  }
  return out;
}
